import pluralize from 'pluralize';
import { ReportingControllerBaseConfig } from '../../../base';
import { ContentOverlayController } from './contentOverlayInterface';
import { capitalize } from 'lodash';
import { assert, assertNever } from '@varos/util-typescript';
import { ContentOverlayViewProps } from './contentOverlayProps';
import { ContentOverlayAggregate } from './contentOverlayModel';
import { Span, BlockAnnotations } from '@varos/research-sdk';
import { useMemo } from 'react';

export function createContentOverlayController(
    config: ReportingControllerBaseConfig
): ContentOverlayController {
    function getSpan(item: Span): string {
        if (item.kind === 'text') {
            const textSpan = item as Span & {
                kind: 'text';
                href?: string | null;
                annotations?: BlockAnnotations;
            };
            let renderedText = textSpan.text;
            const annotations = textSpan.annotations;

            // Apply annotations - order matters for nesting
            if (annotations?.code) {
                renderedText = '`' + renderedText + '`';
            }
            if (annotations?.strikethrough) {
                renderedText = '~~' + renderedText + '~~';
            }
            if (annotations?.italic) {
                renderedText = '*' + renderedText + '*';
            }
            if (annotations?.bold) {
                renderedText = '**' + renderedText + '**';
            }
            // Underline and color are not standard markdown - ignored for now

            // Apply link if href exists
            if (textSpan.href) {
                renderedText = `[${renderedText}](${textSpan.href})`;
            }

            return renderedText;
        }
        if (item.kind === 'reference') {
            return `[^${item.id}]`;
        }
        if (item.kind === 'mention') {
            return `mentions not supported`;
        }
        assertNever(item);
    }
    function getMarkdown(item: ContentOverlayAggregate): string {
        return item.blocks
            .flatMap((block) => {
                if (block.data.kind === 'text') {
                    return [block.data.items.map(getSpan).join(' ')];
                }
                if (block.data.kind === 'heading') {
                    return [
                        [
                            '#'.repeat(block.data.level as number),
                            block.data.items.map(getSpan).join(' '),
                        ].join(' '),
                    ];
                }
                if (block.data.kind === 'divider') {
                    return ['---'];
                }
                if (block.data.kind === 'quote') {
                    return [['> ' + block.data.items.map(getSpan).join(' ')].join('\n')];
                }
                if (block.data.kind === 'code') {
                    return [
                        [
                            '```' +
                                block.data.language +
                                '\n' +
                                block.data.text +
                                '\n```',
                        ].join('\n'),
                    ];
                }
                if (block.data.kind === 'callout') {
                    return [['> ' + block.data.text].join('\n')];
                }
                // assertNever(block.data.kind);
                return [];
            })
            .join('\n\n');
    }
    return {
        useProps(context, item, props): ContentOverlayViewProps {
            // assert(item.content.markdown, 'expected markdown to be set on system source');
            const markdown = useMemo(() => getMarkdown(item), [item]);
            // console.log('content overlay item', item, markdown);
            return {
                breadcrumb: {
                    items: [
                        ...(item.source.type
                            ? [
                                  {
                                      id: 'type',
                                      label: capitalize(
                                          pluralize(item.source.type.replaceAll('-', ' '))
                                      ),
                                      isActive: false,
                                      link: null,
                                  },
                              ]
                            : []),
                        {
                            id: 'provider',
                            label: 'Varos',
                            isActive: false,
                            link: null,
                        },
                        {
                            id: item.source.id,
                            label: item.source.displayName,
                            isActive: true,
                            link: null,
                        },
                    ],
                },
                content: {
                    title: item.source.displayName,
                    markdown,
                    author: {
                        label: item.author.name,
                    },
                    duration: {
                        label: 'Less than 5 min read',
                    },
                    timestamp: {
                        absolute: item.source.createdAt.toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                        }),
                    },
                },
                references: [],
                // references: item.content.references.map((reference, index) => ({
                //     id: reference.id,
                //     number: index + 1,
                //     label: reference.name,
                //     author: reference.author,
                //     year: reference.year,
                //     publisher: reference.publisher,
                //     anchor: reference.url
                //         ? {
                //               href: reference.url,
                //               id: reference.id,
                //           }
                //         : null,
                // })),
            };
        },
    };
}
